import SaveIcon from '@mui/icons-material/Save';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Close,
  Notifications,
  NotificationsOff,
} from '@mui/icons-material';
import {
  Dialog,
  IconButton,
  Paper,
  Tooltip,
  useColorScheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import TimezoneSelect from '../../../../../../components/TimezoneSelect';

import BarnUsers from '../../../../../../components/Autocompletes/BarnUsers';
import { getLocationIcon } from '../../../../../../components/LocationChip';
import {
  addLocation,
  updateLocation,
} from '../../../../../../redux/location/actions';
import { getBarnUsersForAssignmentString } from '../../../../../../utils/scheduleHelper';

const AddEditLocationModalComponent = ({
  open,
  onClose,
  location,
}) => {
  const dispatch = useDispatch();
  const { mode } = useColorScheme();
  // Redux
  const isFetchingLocations = useSelector((state) => state.location.isFetchingLocations);
  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);

  const [addingLocationError, setAddingLocationError] = React.useState(null);

  // form fields
  const [name, setName] = React.useState('');
  const [type, setType] = React.useState('');
  const [assignedPlanner, setAssignedPlanner] = React.useState('');
  const [planningTimeUtcHour, setPlanningTimeUtcHour] = React.useState(17);
  const [timezone, setTimezone] = React.useState('');
  const [validation, setValidation] = React.useState({});

  React.useEffect(() => {
    setName((location && location.name) || '');
    setType((location && location.type) || 'Barn');
    const tz = (location && location.timezone) || Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(tz);
    setAssignedPlanner((location && location.assignedPlanner) || '')
    setPlanningTimeUtcHour((location && location.planningTimeUtcHour && moment().utc()
      .set('hour', location.planningTimeUtcHour).tz(tz)
      .hour()) || 17) // convert back to local timezone hour
    setValidation({});
    setAddingLocationError(null);
  }, [location]);

  const validateFields = () => {
    const updatedValidation = { name: '' };
    let isValid = true;
    if (name.length < 2 || name.length > 100) {
      updatedValidation.name = 'Name is required';
      isValid = false;
    }
    setValidation(updatedValidation);
    return isValid;
  };

  const onSave = () => {
    if (!validateFields()) { return; }

    if (!location) {
      // add horse location
      dispatch(addLocation({
        name,
        type,
        timezone,
        assignedPlanner,
        planningTimeUtcHour: !planningTimeUtcHour
          ? null
          : moment().set('hour', planningTimeUtcHour).utc().hour(),
      })).then((newLocation) => {
        setAddingLocationError(false);
        onClose(newLocation);
      }).catch((error) => {
        setAddingLocationError(error.message || 'There was an error attempting to add the location. Please contact Questri support.');
        console.error(error);
      });
    } else {
      dispatch(updateLocation(location.id, {
        name,
        type,
        timezone,
        assignedPlanner,
        planningTimeUtcHour: !planningTimeUtcHour
          ? null
          : moment().set('hour', planningTimeUtcHour).utc().hour(),
      })).then((updatedLocation) => {
        setAddingLocationError(false);
        onClose(updatedLocation);
      }).catch((error) => {
        setAddingLocationError(error.message || 'There was an error attempting to update the location. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Paper sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            background: mode === 'light'
              ? 'var(--template-palette-primary-main, #1976d2)'
              : 'var(--template-palette-background-paper)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
          }}
        >
          <Typography
            id="modal-add-location-title"
            variant="h5"
            component="h2"
            color={mode === 'light'
              ? '#fff'
              : 'inherit'}
          >
            {location
              ? 'Edit Location'
              : 'Add Location'}
          </Typography>
          <IconButton
            onClick={() => onClose()}
            sx={{
              color: mode === 'light'
                ? '#fff'
                : 'inherit',
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <TextField
                fullWidth
                sx={{ width: '100%' }}
                required
                id="name-field"
                label="Name"
                value={name}
                onChange={(e) => { setName(e.target.value); }}
                disabled={isFetchingLocations}
                error={!!validation.name}
                helperText={validation.name}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="location-type-select-label">Location Type</InputLabel>
                <Select
                  sx={{ width: '100%' }}
                  labelId="location-type-select-label"
                  id="location-type-select"
                  value={type}
                  label="Location Type"
                  disabled={isFetchingLocations}
                  onChange={(e) => setType(e.target.value)}
                  MenuProps={{ style: { maxHeight: 400 } }}
                >
                  <MenuItem value="Barn">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ mr: 1 }}>{getLocationIcon('Barn')}</Box>
                      Barn
                    </Box>
                  </MenuItem>
                  <MenuItem value="Trainer">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ mr: 1 }}>{getLocationIcon('Trainer')}</Box>
                      Trainer
                    </Box>
                  </MenuItem>
                  <MenuItem value="Competition">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ mr: 1 }}>{getLocationIcon('Competition')}</Box>
                      Show
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TimezoneSelect
                label="Location Timezone"
                value={timezone}
                onChange={(e, value) => setTimezone(value)}
                disabled={!!location || isFetchingLocations}
                helperText={location
                  ? 'Cannot be modified after creation'
                  : ''}
              />
            </Grid>
            <Grid size={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 4,
                  mb: 1,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 300 }}>Location Planning Settings</Typography>
                <Box sx={{ width: '65%' }}><Divider /></Box>
              </Box>
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: 12,
                }}
              >
                Each location with active horses will need daily planning. Assign responsible
                user(s) and a daily reminder time to complete the next days plan.
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <BarnUsers
                label="Assigned Planner(s)"
                id="planning-users-select-autocomplete"
                multiple
                value={getBarnUsersForAssignmentString(assignedPlanner, barnUsersMap)}
                onSelect={(e, newBarnUsers) => {
                  // take selected barn users and map to assignment syntax @[NAME](AUTH0_USER_ID)
                  const locationPlanners = newBarnUsers.map((bu) => (bu.id
                    ? `@[${bu.userFullName}](${bu.auth0UserId || bu.email})`
                    : `@[${bu.userFullName || bu}](${null})`)).join('');
                  setAssignedPlanner(locationPlanners);
                }}
                limitTags={1}
              />
            </Grid>
            <Grid
              size={{
                xs: 10,
                md: 5,
              }}
            >
              <FormControl
                size="small"
                fullWidth
              >
                <InputLabel>Reminder Time</InputLabel>
                <Select
                  value={planningTimeUtcHour}
                  label="Reminder Time"
                  onChange={(e) => setPlanningTimeUtcHour(e.target.value)}
                  MenuProps={{ style: { maxHeight: 400 } }}
                >
                  <MenuItem key={0} value={0}>12am</MenuItem>
                  <MenuItem key={1} value={1}>1am</MenuItem>
                  <MenuItem key={2} value={2}>2am</MenuItem>
                  <MenuItem key={3} value={3}>3am</MenuItem>
                  <MenuItem key={4} value={4}>4am</MenuItem>
                  <MenuItem key={5} value={5}>5am</MenuItem>
                  <MenuItem key={6} value={6}>6am</MenuItem>
                  <MenuItem key={7} value={7}>7am</MenuItem>
                  <MenuItem key={8} value={8}>8am</MenuItem>
                  <MenuItem key={9} value={9}>9am</MenuItem>
                  <MenuItem key={10} value={10}>10am</MenuItem>
                  <MenuItem key={11} value={11}>11am</MenuItem>
                  <MenuItem key={12} value={12}>12pm</MenuItem>
                  <MenuItem key={13} value={13}>1pm</MenuItem>
                  <MenuItem key={14} value={14}>2pm</MenuItem>
                  <MenuItem key={15} value={15}>3pm</MenuItem>
                  <MenuItem key={16} value={16}>4pm</MenuItem>
                  <MenuItem key={17} value={17}>5pm</MenuItem>
                  <MenuItem key={18} value={18}>6pm</MenuItem>
                  <MenuItem key={19} value={19}>7pm</MenuItem>
                  <MenuItem key={20} value={20}>8pm</MenuItem>
                  <MenuItem key={21} value={21}>9pm</MenuItem>
                  <MenuItem key={22} value={22}>10pm</MenuItem>
                  <MenuItem key={23} value={23}>11pm</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 2,
                md: 1,
              }}
            >
              <Tooltip
                title={planningTimeUtcHour
                  ? 'Notifications On: Click to turn off daily mobile notifications for planning at this location'
                  : 'Notifications Off: Click to turn on and receive a daily mobile notification reminder to do planning at this location'}
              >
                <IconButton
                  color="secondary"
                  onClick={() => {
                    setPlanningTimeUtcHour(planningTimeUtcHour
                      ? null
                      : (location
                        && location.planningTimeUtcHour
                        && moment().utc().set('hour', location.planningTimeUtcHour).tz((location && location.timezone) || Intl.DateTimeFormat().resolvedOptions().timeZone)
                          .hour()) || 17)
                  }}
                >
                  {planningTimeUtcHour
                    ? <Notifications />
                    : <NotificationsOff />}
                </IconButton>
              </Tooltip>
            </Grid>

            {
              addingLocationError && <Grid size={12}><Typography variant="caption" color="error" gutterBottom>{addingLocationError}</Typography></Grid>
            }

            <Grid align="right" size={12}>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="warning" sx={{ mb: 1 }}>
                <div>Note: Once a location is created, the timezone cannot be modified.</div>
                {!location && <div>Please ensure this is correct before saving.</div>}
              </Alert>
              <Button onClick={onSave} disabled={isFetchingLocations} variant="contained" color="primary" loading={isFetchingLocations} loadingPosition="start" startIcon={<SaveIcon />}>Save</Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Dialog>
  );
};

AddEditLocationModalComponent.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  location: PropTypes.object,
};

AddEditLocationModalComponent.defaultProps = {
  open: false,
  location: null,
};

export default (AddEditLocationModalComponent);
